import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { ACTUAL_CLOSING_DATE, ATTACHMENT_ID, CLOSING_ACK, OWNERSHIP_PROOF_DOCS, FILE_SIZE, FILE_TYPE, IS_REQUIRED, OCP_ID, PACKET_DOC_ID, PROOF_FILE, PROOF_TYPE, STATUS, UPLOADED_STATUS, PROOF_DOCS } from "../../consts";
import { formValues, schema } from "./validations";
import { zodResolver } from "@hookform/resolvers/zod";
import { getRequiredFields } from "@/lib/utils";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useAddOCPClosingDocumentMutation, useDeleteOCPClosingDocumentMutation, useGetClosingProofList, useGetOCPDocumentList, useUpdateOCPClosingDocumentMutation } from "@/services";
import { toast } from "@/components/Toast";
import { setOCPData } from "@/redux/slices";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

const useStep4 = ({onBaseSubmit}: {onBaseSubmit: any}) => {
  const {action} = useParams();
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const ocpData = useSelector((state: RootState) => state.currentData.ocpData);
  const pendingBillData = useSelector((state: RootState) => state.currentData.pendingBillData);
  const communityData = useSelector((state: RootState) => state.currentData.communityData);
  const dispatch = useDispatch();
  const currAction = useRef<"save" | "email">("save");
  const deletePacketDocId = useRef<string[]>([]);
  const queryClient = useQueryClient();
  const isEdit = action == "edit";
  const [updatedKey, setUpdatedKey] = useState<number>(Math.random() * 2);

  const form = useForm<formValues>({
    defaultValues: {
      // step 3
      [OWNERSHIP_PROOF_DOCS]: [{
        [PACKET_DOC_ID]: "",
        [ATTACHMENT_ID]: "",
        [PROOF_TYPE]: "",
        [IS_REQUIRED]: false,
        [FILE_SIZE]: "",
        [FILE_TYPE]: "",
        [PROOF_FILE]: null,
        [UPLOADED_STATUS]: false
      }],
      [STATUS]: "OWNERSHIP_CHANGE_DOC"
    },
    resolver: zodResolver(schema),
  });
  
  const requiredFields = getRequiredFields(schema);
  const formData: any = useWatch({ control: form.control });

  useEffect(() => {
    queryClient.invalidateQueries({queryKey: ["ocp-document-types", communityData?.communityId]})
    queryClient.invalidateQueries({ queryKey: ["closing-proof-list", ocpData?.[OCP_ID]]})
  }, []);

  const { data: ocpDocList, isLoading: isDocListLoading } = useGetOCPDocumentList({
    communityId: communityData?.communityId,
    ocpStage: "OWNERSHIP_CHANGE_DOC",
    queryParams: { enabled: !!communityData?.communityId },
  });
  
  const { data: closingProofList, isLoading: isLoadingProofList } = useGetClosingProofList({
    ocpId: ocpData?.[OCP_ID],
    ocpStage: "OWNERSHIP_CHANGE_DOC",
    queryParams: {
      enabled: !!ocpData?.[OCP_ID]
    }
  });

  useEffect(() => {
    if (ocpDocList?.data) {
      const list = ocpDocList?.data?.filter((data: any) => data?.requirementType == "REQUIRED")?.map((data: any) => {
        const closingDoc = closingProofList?.data?.documents?.find((val: any) => val?.documentTypeId == data?.documentTypeId);
        const fileName = closingDoc?.originalFileName || closingDoc?.fileName;
        
        return {
          [ATTACHMENT_ID]: closingDoc?.[ATTACHMENT_ID]?.toString() || "",
          [PACKET_DOC_ID]: closingDoc?.[PACKET_DOC_ID]?.toString(),
          [PROOF_TYPE]: data?.documentTypeId?.toString(),
          [PROOF_FILE]: fileName ? {name: fileName, size: closingDoc?.fileSize, url: closingDoc?.presignedUrl} : null,
          [IS_REQUIRED]: data?.requirementType == "REQUIRED",
          [FILE_SIZE]: data[FILE_SIZE]?.toString() || "",
          [FILE_TYPE]: data?.[FILE_TYPE]?.length ? data?.[FILE_TYPE]?.map((val: any) => `.${val}`)?.join(",") : "",
          [UPLOADED_STATUS]: closingDoc?.[UPLOADED_STATUS] || false
        }
      });

      form.setValue(OWNERSHIP_PROOF_DOCS, list?.length ? list : [{
        [PACKET_DOC_ID]: "",
        [ATTACHMENT_ID]: "",
        [PROOF_TYPE]: "",
        [IS_REQUIRED]: false,
        [FILE_SIZE]: "",
        [FILE_TYPE]: "",
        [PROOF_FILE]: null,
        [UPLOADED_STATUS]: false
      }])
      setUpdatedKey(Math.random() * 3);
    }
  }, [ocpDocList, closingProofList]);

  const proofDocs = useWatch({ control: form.control, name: OWNERSHIP_PROOF_DOCS }) || [];

  const onRemoveProof = (index: number) => () => {
    delete fileInputRefs.current[index];
    if ((proofDocs as any)?.[index]?.[PACKET_DOC_ID]) {
      deletePacketDocId.current.push((proofDocs as any)?.[index]?.[PACKET_DOC_ID])
    }
    form.setValue(
      OWNERSHIP_PROOF_DOCS,
      proofDocs.filter((_: any, idx: number) => index !== idx)
    );
  };

  const onAddMore = () => {
    form.setValue(OWNERSHIP_PROOF_DOCS, [
      ...proofDocs,
      { [ATTACHMENT_ID]: "",
        [PROOF_TYPE]: "",
        [IS_REQUIRED]: false,
        [FILE_SIZE]: "",
        [FILE_TYPE]: "",
        [PROOF_FILE]: null,
        [UPLOADED_STATUS]: false
      },
    ]);
  };

  const onViewAttachment = (url: string) => {
    window.open(url, "_blank");
  };

  const enabledSendEmail = useMemo(() => formData[OWNERSHIP_PROOF_DOCS]?.filter((data: any) => data?.[IS_REQUIRED] && data?.[PROOF_FILE])?.length > 0 && formData[CLOSING_ACK], [formData[OWNERSHIP_PROOF_DOCS], formData[CLOSING_ACK]])

  const {
    mutateAsync: addClosingProof,
    isPending: isAddDocPending,
  } = useAddOCPClosingDocumentMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
    },
  });

  const {
    mutateAsync: updateClosingProof,
    isPending: isUpdateDocPending,
  } = useUpdateOCPClosingDocumentMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
    },
  });

  const {
    mutateAsync: deleteClosingProof,
    isPending: isDeleteDocPending,
  } = useDeleteOCPClosingDocumentMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
    },
  });
  
  const validateClosingDoc = (data: any) => {
    const docs = data[PROOF_DOCS] || [];

    for (const doc of docs) {
      const isRequired = Boolean(doc?.[IS_REQUIRED]);
      const hasFile = Boolean(doc?.[PROOF_FILE]);
      const hasType = Boolean(doc?.[PROOF_TYPE]);

      // Rule 1: If doc is required and proof file is missing -> Error
      if (isRequired && !hasFile) {
        toast.error("Please upload the required proof file.");
        return false;
      }

      // Rule 2: If optional (not required), type is selected, but file is missing -> Error
      // (If both type and file are empty, it passes safely as valid optional row)
      if (!isRequired && hasType && !hasFile) {
        toast.error("Please upload a proof file for the selected proof type.");
        return false;
      }
    }

    return true;
  }

  const uploadClosingDoc = async (ocpId: string, docsToUpload: any[]) => {
    const docExist = docsToUpload?.filter(
      (file: any) => file?.[PROOF_FILE] instanceof File
    ) || [];

    let uploadPromises: any[] = [];

    if (deletePacketDocId.current?.length) {
      deletePacketDocId.current?.forEach((packageDocId: string) => {
        uploadPromises.push(
          deleteClosingProof({
            ocpId: ocpId.toString(),
            packageDocId
          })
        )
      })
      
    }

    if (docExist.length) {
      docExist.map((file: any) => {
        const formData = new FormData();
        formData.append("file", file?.[PROOF_FILE]);
        formData.append("ocpStage", "OWNERSHIP_CHANGE_DOC");
        formData.append("documentTypeId", file?.[PROOF_TYPE]);
  
        if (file[PACKET_DOC_ID]) {
          uploadPromises.push(updateClosingProof({
            ocpId: ocpId.toString(),
            packageDocId: file?.[PACKET_DOC_ID],
            data: formData,
          }));
        } else {
          uploadPromises.push(addClosingProof({
            ocpId: ocpId.toString(),
            data: formData,
          }));
        }
      });
  
      try {
        // 1. Wait for all mutation promises to resolve
        const results = await Promise.all(uploadPromises);
  
        // 2. Extract success payload data from response
        const newlyUploadedFiles = results.map((res: any, index: number) => ({
          ...res?.data, // API response data (e.g., attachmentId, filePath, etc.)
          proofType: docExist[index]?.[PROOF_TYPE],
          fileName: docExist[index]?.[PROOF_FILE]?.name,
        }));
  
        // 3. Update React state (append to existing state)
        dispatch(setOCPData({ ...ocpData, [OWNERSHIP_PROOF_DOCS]: [...newlyUploadedFiles] }))
  
        return newlyUploadedFiles;
      } catch (error) {
        console.error("One or more files failed to upload:", error);
        throw error;
      }
    }
  };

  const onSubmit = async (data: any) => {
    const isClosingDocValid = validateClosingDoc(data);
    if (!isClosingDocValid) return;

    try {
      // Step 3 Requirement: Upload documents BEFORE calling addOwnershipChangePacket
      await uploadClosingDoc(ocpData[OCP_ID], data[OWNERSHIP_PROOF_DOCS]);
    } catch (error) {
      console.error("Error uploading proof documents:", error);
      return; // Stop execution if document uploads fail
    }

    onBaseSubmit({
      [ACTUAL_CLOSING_DATE]: data[ACTUAL_CLOSING_DATE],
      [OCP_ID]: ocpData[OCP_ID],
      [STATUS]: data[STATUS],
    })
  }

  const onSubmitClick = (action: "email" | "save") => {
    currAction.current = action;
  };

  const disabledSaveNext = useMemo(() => pendingBillData?.isPending && !formData[CLOSING_ACK] , [pendingBillData, formData[CLOSING_ACK]]);

  useEffect(() => {
    if (isEdit && closingProofList) {
      form.setValue(CLOSING_ACK, !!closingProofList?.data?.documents?.length);
    }
  }, [closingProofList]);

  return {
    form,
    requiredFields,
    fileInputRefs,
    proofDocs,
    onRemoveProof,
    onAddMore,
    isDocListLoading: isDocListLoading || isLoadingProofList,
    ocpDocList: ocpDocList?.data || [],
    formData,
    onViewAttachment,
    enabledSendEmail,
    onSubmit,
    isAddDocPending: isAddDocPending || isUpdateDocPending || isDeleteDocPending,
    onSubmitClick,
    updatedKey,
    isBillPending: pendingBillData?.isPending,
    disabledSaveNext
  };
};

export default useStep4;